import { motion, useInView } from "framer-motion";
import { useRef } from "react";

const locations = [
  {
    src: "https://images.unsplash.com/photo-1769247569755-5b22a7a82def?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwzfHxBdWNrbGFuZCUyMENCRCUyMHNreWxpbmUlMjBhZXJpYWwlMjBkcm9uZSUyME5ldyUyMFplYWxhbmR8ZW58MHx8fHwxNzc1NjEyMzY4fDA&ixlib=rb-4.1.0&q=85",
    alt: "Auckland CBD skyline",
    label: "Tamaki Makaurau",
    sublabel: "Auckland",
    desc: "Strategic partnerships and economic development across Aotearoa's largest city.",
  },
  {
    src: "https://images.unsplash.com/photo-1707912259002-8b328e6ec5a4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHxOZXclMjBaZWFsYW5kJTIwTWFvcmklMjBjYXJ2ZWQlMjBob3VzZSUyMHRyYWRpdGlvbmFsfGVufDB8fHx8MTc3NTYxMjU0MXww&ixlib=rb-4.1.0&q=85",
    alt: "Traditional Maori carved meeting house",
    label: "Te Ao Maori",
    sublabel: "Culture & Community",
    desc: "Grounded in te ao Maori, our work is shaped by Indigenous knowledge systems.",
  },
  {
    src: "https://images.unsplash.com/photo-1624589010805-b4e69450ed87?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwzfHxXZWxsaW5ndG9uJTIwQ0JEJTIwc2t5bGluZSUyMGhhcmJvdXIlMjBOZXclMjBaZWFsYW5kJTIwYWVyaWFsfGVufDB8fHx8MTc3NTYxMjUyN3ww&ixlib=rb-4.1.0&q=85",
    alt: "Wellington CBD and harbour at sunset",
    label: "Te Whanganui-a-Tara",
    sublabel: "Wellington",
    desc: "Governance, policy, and system design at the heart of government.",
  },
];

export default function CinematicBreak() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <section ref={ref} data-testid="cinematic-break" className="relative">
      {/* Section header */}
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-16 pb-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
        >
          <p className="text-sm font-bold tracking-[0.2em] uppercase text-[#bb321a] mb-4">
            Where We Work
          </p>
          <h2 className="font-['Syne'] text-3xl sm:text-4xl font-bold text-white">
            Across <span className="text-[#364225]">Aotearoa</span> and the Pacific
          </h2>
        </motion.div>
      </div>

      {/* Location cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-0">
        {locations.map((loc, i) => (
          <motion.div
            key={loc.label}
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : {}}
            transition={{ duration: 0.6, delay: i * 0.15 }}
            className="relative h-72 md:h-96 overflow-hidden group"
          >
            <img loading="lazy"
              src={loc.src}
              alt={loc.alt}
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
            <div className="absolute bottom-6 left-6 right-6">
              <p className="text-[#bb321a] font-bold text-xs uppercase tracking-wider mb-1">
                {loc.sublabel}
              </p>
              <p className="text-white font-['Syne'] font-bold text-xl sm:text-2xl mb-2">
                {loc.label}
              </p>
              <p className="text-white/80 text-sm leading-relaxed">
                {loc.desc}
              </p>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Video section */}
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Video 1 - Maori Economy */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <div className="relative aspect-video bg-black/50 border border-white/10 overflow-hidden">
              <iframe
                src="https://www.youtube.com/embed/XYo8Jo3Fxl4?rel=0&modestbranding=1"
                title="Maori Economics Shapes Our Future"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                data-testid="video-embed-1"
                className="w-full h-full"
                style={{ border: "none" }}
              />
            </div>
            <p className="text-[#bb321a] text-xs font-bold uppercase tracking-wider mt-3">
              Te Ohanga Maori
            </p>
            <p className="text-white/50 text-sm mt-1">
              How Maori economics shapes our future - the $126 billion asset base driving Indigenous-led growth
            </p>
          </motion.div>

          {/* Video 2 - Maori Economic Investment */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <div className="relative aspect-video bg-black/50 border border-white/10 overflow-hidden">
              <iframe
                src="https://www.youtube.com/embed/kWf_qD6Ib_4?rel=0&modestbranding=1"
                title="Oohanga ki Te Ao - Maori Economic Investment Summit"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                data-testid="video-embed-2"
                className="w-full h-full"
                style={{ border: "none" }}
              />
            </div>
            <p className="text-[#bb321a] text-xs font-bold uppercase tracking-wider mt-3">
              Ohanga ki Te Ao
            </p>
            <p className="text-white/50 text-sm mt-1">
              The inaugural Maori Economic Investment Summit - Indigenous innovation on the global stage
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
